import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { 
  Award, 
  Shield, 
  Clock, 
  Star, 
  Users, 
  TrendingUp, 
  Lock, 
  CheckCircle2, 
  Sparkles,
  AlertTriangle,
  ArrowRight,
  Building2,
  Globe,
  Calendar,
  Zap,
  Target,
  Crown,
  ChevronDown,
  X,
  Eye,
  EyeOff,
  Scale,
  FileCheck
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { getAvailableSports } from '@/config/sportsConfig';
import { validatePassword, isPasswordValid, getPasswordStrength, PasswordRequirements } from '@/utils/passwordUtils';
import bcrypt from 'bcryptjs';

// Countries and states data
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' },
  { code: 'JP', name: 'Japan' },
  { code: 'RU', name: 'Russia' },
  { code: 'KR', name: 'South Korea' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'OTHER', name: 'Other' },
];

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' },
];

const STATES_BY_COUNTRY: Record<string, { code: string; name: string }[]> = {
  US: US_STATES,
  CA: [
    { code: 'AB', name: 'Alberta' }, { code: 'BC', name: 'British Columbia' }, { code: 'MB', name: 'Manitoba' },
    { code: 'NB', name: 'New Brunswick' }, { code: 'NL', name: 'Newfoundland and Labrador' },
    { code: 'NS', name: 'Nova Scotia' }, { code: 'ON', name: 'Ontario' }, { code: 'PE', name: 'Prince Edward Island' },
    { code: 'QC', name: 'Quebec' }, { code: 'SK', name: 'Saskatchewan' }, { code: 'NT', name: 'Northwest Territories' },
    { code: 'NU', name: 'Nunavut' }, { code: 'YT', name: 'Yukon' },
  ],
  GB: [
    { code: 'ENG', name: 'England' }, { code: 'SCT', name: 'Scotland' },
    { code: 'WAL', name: 'Wales' }, { code: 'NIR', name: 'Northern Ireland' },
  ],
  AU: [
    { code: 'NSW', name: 'New South Wales' }, { code: 'QLD', name: 'Queensland' },
    { code: 'SA', name: 'South Australia' }, { code: 'TAS', name: 'Tasmania' },
    { code: 'VIC', name: 'Victoria' }, { code: 'WA', name: 'Western Australia' },
    { code: 'ACT', name: 'Australian Capital Territory' }, { code: 'NT', name: 'Northern Territory' },
  ],
};

const AgentBetaLanding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [foundingCount, setFoundingCount] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedSports, setSelectedSports] = useState<string[]>([]);
  const [selectedLicensedStates, setSelectedLicensedStates] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirements>({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false
  });
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    company_name: '',
    applicant_type: '',
    other_type_description: '',
    experience_years: '',
    athletes_represented: '',
    country: '',
    state_province: '',
    city: '',
    why_join: '',
    how_heard_about_us: '',
    linkedin_url: '',
    website_url: '',
    // New credential fields
    username: '',
    password: '',
    confirm_password: '',
    // NIL compliance fields
    is_licensed_attorney: false,
    has_liability_insurance: false,
    sparta_compliance_acknowledged: false,
    nil_experience_years: ''
  });

  const availableSports = getAvailableSports();
  const availableStates = formData.country ? STATES_BY_COUNTRY[formData.country] || [] : [];

  useEffect(() => {
    const fetchFoundingCount = async () => {
      const { data, error } = await supabase.rpc('get_founding_agent_count');
      if (!error && data !== null) {
        setFoundingCount(data);
      }
    };
    fetchFoundingCount();
  }, []);

  useEffect(() => {
    setPasswordRequirements(validatePassword(formData.password));
  }, [formData.password]);

  const spotsRemaining = Math.max(0, 200 - foundingCount);
  const showExactCount = spotsRemaining <= 10;

  const handleSportToggle = (sport: string) => {
    setSelectedSports(prev => 
      prev.includes(sport) 
        ? prev.filter(s => s !== sport)
        : [...prev, sport]
    );
  };

  const handleLicensedStateToggle = (stateCode: string) => {
    setSelectedLicensedStates(prev => 
      prev.includes(stateCode) 
        ? prev.filter(s => s !== stateCode)
        : [...prev, stateCode]
    );
  };

  const validateForm = (): string | null => {
    if (!formData.username || formData.username.length < 4) {
      return 'Username must be at least 4 characters';
    }
    if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      return 'Username can only contain letters, numbers, and underscores';
    }
    if (!isPasswordValid(passwordRequirements)) {
      return 'Password does not meet all requirements';
    }
    if (formData.password !== formData.confirm_password) {
      return 'Passwords do not match';
    }
    if (!formData.sparta_compliance_acknowledged) {
      return 'You must acknowledge SPARTA compliance';
    }
    if (formData.applicant_type === 'other' && (!formData.other_type_description || formData.other_type_description.trim().length < 20)) {
      return 'Please provide a detailed description (at least 20 characters) of what you do and how you help athletes';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      setIsSubmitting(false);
      return;
    }

    // Check username availability
    const { data: usernameCheck } = await supabase.rpc('is_username_taken', {
      username_to_check: formData.username
    });
    
    if (usernameCheck) {
      toast.error('Username is already taken. Please choose a different one.');
      setIsSubmitting(false);
      return;
    }

    // Hash password
    const passwordHash = await bcrypt.hash(formData.password, 10);

    // Build geographic focus string
    const geographicParts = [];
    if (formData.city) geographicParts.push(formData.city);
    if (formData.state_province) {
      const stateObj = availableStates.find(s => s.code === formData.state_province);
      if (stateObj) geographicParts.push(stateObj.name);
    }
    if (formData.country) {
      const countryObj = COUNTRIES.find(c => c.code === formData.country);
      if (countryObj) geographicParts.push(countryObj.name);
    }

    try {
      const { error } = await supabase.from('founding_agent_applications').insert({
        user_id: user?.id || null,
        email: formData.email,
        full_name: formData.full_name,
        company_name: formData.company_name || null,
        applicant_type: formData.applicant_type,
        experience_years: formData.experience_years ? parseInt(formData.experience_years) : null,
        athletes_represented: formData.athletes_represented ? parseInt(formData.athletes_represented) : null,
        sports_focus: selectedSports.length > 0 ? selectedSports : null,
        geographic_focus: geographicParts.length > 0 ? geographicParts : null,
        why_join: formData.why_join || null,
        how_heard_about_us: formData.how_heard_about_us || null,
        linkedin_url: formData.linkedin_url || null,
        website_url: formData.website_url || null,
        // New fields
        username: formData.username.toLowerCase(),
        password_hash: passwordHash,
        user_type: 'internal',
        states_licensed: selectedLicensedStates.length > 0 ? selectedLicensedStates : [],
        is_licensed_attorney: formData.is_licensed_attorney,
        has_liability_insurance: formData.has_liability_insurance,
        sparta_compliance_acknowledged: formData.sparta_compliance_acknowledged,
        nil_experience_years: formData.nil_experience_years ? parseInt(formData.nil_experience_years) : null,
        other_type_description: formData.applicant_type === 'other' ? formData.other_type_description : null
      } as any);

      if (error) throw error;

      toast.success('Application submitted successfully! You can now check your application status.');
      navigate('/agent/application-status');
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: <Clock className="h-7 w-7" />,
      title: '24 Months Access',
      description: 'Pay for one year, receive 24 months of platform access',
      highlight: '2X VALUE'
    },
    {
      icon: <Lock className="h-7 w-7" />,
      title: 'Lifetime Price Lock',
      description: 'Your founding rate is locked as long as your subscription remains active',
      highlight: 'FOREVER'
    },
    {
      icon: <Crown className="h-7 w-7" />,
      title: 'Founding Agent Badge',
      description: 'Exclusive badge displayed on your profile within the platform',
      highlight: 'EXCLUSIVE'
    },
    {
      icon: <Zap className="h-7 w-7" />,
      title: 'Priority Feature Access',
      description: 'Early access to new NIL and agent tools as they are released',
      highlight: 'FIRST ACCESS'
    },
    {
      icon: <Target className="h-7 w-7" />,
      title: 'Roadmap Influence',
      description: 'Early feature feedback and ability to shape platform evolution',
      highlight: 'YOUR VOICE'
    },
    {
      icon: <Shield className="h-7 w-7" />,
      title: 'Priority Support',
      description: 'Dedicated support during the beta phase',
      highlight: 'VIP'
    }
  ];

  const scrollToApply = () => {
    document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-black via-[#0a0a0a] to-[#0d1a0d] pt-28 md:pt-32 pb-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-primary/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 animate-fade-in ${
              showExactCount 
                ? 'bg-red-500/20 border border-red-500/40' 
                : 'bg-orange-500/20 border border-orange-500/40'
            }`}>
              <span className="relative flex h-3 w-3">
                <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  showExactCount ? 'bg-red-400 animate-ping' : 'bg-orange-400'
                }`}></span>
                <span className={`relative inline-flex rounded-full h-3 w-3 ${
                  showExactCount ? 'bg-red-500' : 'bg-orange-500'
                }`}></span>
              </span>
              <span className={`text-sm font-bold uppercase tracking-wider ${
                showExactCount ? 'text-red-400' : 'text-orange-400'
              }`}>
                {showExactCount ? (
                  <span className="animate-pulse">🔥 Only {spotsRemaining} Spots Left!</span>
                ) : (
                  "Limited to 200 Spots — Don't Miss Out"
                )}
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
              <span className="text-white">Become a </span>
              <span className="bg-gradient-to-r from-primary via-primary to-green-400 bg-clip-text text-transparent">
                Founding Agent
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-4">
              Build the future of athlete opportunity—before the rest of the industry arrives.
            </p>

            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10">
              Get exclusive early access to powerful NIL and athlete discovery tools. 
              Be among the first 200 agents shaping the platform.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={scrollToApply}
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-10 py-7 text-lg rounded-xl shadow-[0_0_40px_rgba(0,255,0,0.3)] hover:shadow-[0_0_60px_rgba(0,255,0,0.4)] transition-all duration-300"
              >
                Apply Now — It's Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/agent/pricing')}
                className="border-white/20 text-white hover:bg-white/10 font-semibold px-8 py-7 text-lg rounded-xl"
              >
                View Pricing Details
              </Button>
            </div>

            {/* Check Application Status Link */}
            <p className="mt-6 text-muted-foreground">
              Already applied?{' '}
              <Button 
                variant="link" 
                className="text-primary p-0 h-auto"
                onClick={() => navigate('/agent/application-status')}
              >
                Check your application status
              </Button>
            </p>
          </div>
        </div>
      </section>

      {/* Social Proof / Trust Bar */}
      <section className="bg-[#0d0d0d] border-y border-white/5 py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-gray-400">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              <span className="text-sm">No upfront payment required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              <span className="text-sm">2-3 day review process</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              <span className="text-sm">Cancel anytime</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              <span className="text-sm text-yellow-400">7-day payment window after approval</span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-b from-[#0d0d0d] to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/30 px-4 py-1.5">
              EXCLUSIVE BENEFITS
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              What Founding Agents Get
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Early supporters receive exclusive perks that will never be available again
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <Card 
                key={index} 
                className="group relative bg-card/50 backdrop-blur border-white/5 hover:border-primary/30 transition-all duration-300 overflow-hidden"
              >
                <div className="absolute top-4 right-4">
                  <span className="text-[10px] font-bold bg-primary/20 text-primary px-2 py-1 rounded-full">
                    {benefit.highlight}
                  </span>
                </div>
                
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <CardHeader className="relative">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform duration-300">
                    {benefit.icon}
                  </div>
                  <CardTitle className="text-xl">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent className="relative">
                  <CardDescription className="text-base">{benefit.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Clarification */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="relative bg-gradient-to-br from-yellow-500/10 to-orange-500/5 border border-yellow-500/30 rounded-2xl p-8 md:p-10">
            <div className="absolute -top-4 left-8">
              <div className="bg-yellow-500 text-black text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                IMPORTANT
              </div>
            </div>
            
            <h3 className="text-2xl font-bold text-yellow-400 mb-4 mt-2">
              Platform Role Clarification
            </h3>
            <p className="text-lg text-yellow-200/90 font-medium mb-4">
              Players Lockeroom provides tools; it is not acting as representation or agent.
            </p>
            <p className="text-yellow-200/70">
              We do not negotiate NIL deals, solicit agreements, receive compensation tied to 
              NIL earnings, or provide legal, financial, or contract advice. All communications 
              and transactions occur solely between users.
            </p>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 bg-gradient-to-b from-background to-[#0d0d0d]" id="apply">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/30 px-4 py-1.5">
                FREE APPLICATION
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Apply for Founding Agent Beta
              </h2>
              <p className="text-muted-foreground">
                Applications are reviewed within 2-3 business days. No payment required to apply.
              </p>
            </div>

            <Card className="border-2 border-primary/20 bg-card/80 backdrop-blur shadow-[0_0_60px_rgba(0,255,0,0.1)]">
              <CardContent className="pt-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Account Credentials Section */}
                  <div className="space-y-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Lock className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold text-lg">Account Credentials</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Create your login credentials to track your application status.
                    </p>

                    <div className="space-y-2">
                      <Label htmlFor="username" className="text-sm font-medium">Username *</Label>
                      <Input
                        id="username"
                        value={formData.username}
                        onChange={(e) => {
                          // Allow letters (upper/lower), numbers, and underscores only
                          const value = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                          setFormData({...formData, username: value});
                        }}
                        required
                        className="bg-background/50 border-white/10 focus:border-primary h-12"
                        placeholder="Choose a unique username"
                      />
                      <p className="text-xs text-muted-foreground">
                        Letters, numbers, and underscores only. Minimum 4 characters.
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-sm font-medium">Password *</Label>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                            required
                            className="bg-background/50 border-white/10 focus:border-primary h-12 pr-10"
                            placeholder="Create a strong password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm_password" className="text-sm font-medium">Confirm Password *</Label>
                        <div className="relative">
                          <Input
                            id="confirm_password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={formData.confirm_password}
                            onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                            required
                            className="bg-background/50 border-white/10 focus:border-primary h-12 pr-10"
                            placeholder="Confirm your password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Password Requirements */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className={`flex items-center gap-1 ${passwordRequirements.minLength ? 'text-green-500' : 'text-muted-foreground'}`}>
                        <CheckCircle2 className="h-3 w-3" /> 8+ characters
                      </div>
                      <div className={`flex items-center gap-1 ${passwordRequirements.hasUppercase ? 'text-green-500' : 'text-muted-foreground'}`}>
                        <CheckCircle2 className="h-3 w-3" /> Uppercase letter
                      </div>
                      <div className={`flex items-center gap-1 ${passwordRequirements.hasLowercase ? 'text-green-500' : 'text-muted-foreground'}`}>
                        <CheckCircle2 className="h-3 w-3" /> Lowercase letter
                      </div>
                      <div className={`flex items-center gap-1 ${passwordRequirements.hasNumber ? 'text-green-500' : 'text-muted-foreground'}`}>
                        <CheckCircle2 className="h-3 w-3" /> Number
                      </div>
                      <div className={`flex items-center gap-1 ${passwordRequirements.hasSpecialChar ? 'text-green-500' : 'text-muted-foreground'}`}>
                        <CheckCircle2 className="h-3 w-3" /> Special character
                      </div>
                      <div className={`flex items-center gap-1 ${formData.password && formData.password === formData.confirm_password ? 'text-green-500' : 'text-muted-foreground'}`}>
                        <CheckCircle2 className="h-3 w-3" /> Passwords match
                      </div>
                    </div>
                  </div>

                  {/* Personal Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="full_name" className="text-sm font-medium">Full Name *</Label>
                      <Input
                        id="full_name"
                        value={formData.full_name}
                        onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                        required
                        className="bg-background/50 border-white/10 focus:border-primary h-12"
                        placeholder="John Smith"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        className="bg-background/50 border-white/10 focus:border-primary h-12"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company_name" className="text-sm font-medium">Company/Agency Name</Label>
                      <Input
                        id="company_name"
                        value={formData.company_name}
                        onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                        className="bg-background/50 border-white/10 focus:border-primary h-12"
                        placeholder="Optional"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="applicant_type" className="text-sm font-medium">I am a... *</Label>
                      <Select 
                        value={formData.applicant_type}
                        onValueChange={(value) => setFormData({...formData, applicant_type: value})}
                      >
                        <SelectTrigger className="bg-background/50 border-white/10 focus:border-primary h-12">
                          <SelectValue placeholder="Select your role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="registered_agent">Registered Agent</SelectItem>
                          <SelectItem value="unregistered_agent">Unregistered Agent</SelectItem>
                          <SelectItem value="pro_certified_agent">Pro-Certified Agent</SelectItem>
                          <SelectItem value="nil_lawyer">NIL Lawyer</SelectItem>
                          <SelectItem value="nil_advisor">NIL Advisor (non-licensed)</SelectItem>
                          <SelectItem value="nil_scout">NIL Scout</SelectItem>
                          <SelectItem value="marketing_consultant">Marketing Advisor</SelectItem>
                          <SelectItem value="independent_agent">Independent Agent</SelectItem>
                          <SelectItem value="recruiter">Recruiter</SelectItem>
                          <SelectItem value="scout">Scout</SelectItem>
                          <SelectItem value="camp_organizer">Camp Organizer</SelectItem>
                          <SelectItem value="academy">Academy</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.applicant_type && formData.applicant_type !== 'other' && (
                        <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-md">
                          <p className="text-xs text-blue-300">
                            {formData.applicant_type === 'registered_agent' && 'Complies with specific state laws (UAAA) and university reporting rules.'}
                            {formData.applicant_type === 'unregistered_agent' && 'Operates without official state filing; high risk for the athlete.'}
                            {formData.applicant_type === 'pro_certified_agent' && 'Licensed by a pro league (NFLPA/NBPA); must strictly separate NIL from pro-contract talks.'}
                            {formData.applicant_type === 'nil_lawyer' && 'Licensed attorney; provides legal protection and contract review (often exempt from agent registration).'}
                            {formData.applicant_type === 'nil_advisor' && 'Assists athletes with NIL opportunities without a legal or agent license.'}
                            {formData.applicant_type === 'nil_scout' && 'Evaluates an athlete\'s marketability (social media, public image) for brands or collectives.'}
                            {formData.applicant_type === 'marketing_consultant' && 'Focuses on branding/social media; may not need an agent license if they don\'t negotiate contracts.'}
                            {formData.applicant_type === 'independent_agent' && 'Works independently to represent athletes in various capacities.'}
                            {formData.applicant_type === 'recruiter' && 'Focuses on discovering and recruiting athletic talent.'}
                            {formData.applicant_type === 'scout' && 'Evaluates athletes for teams, agencies, or programs.'}
                            {formData.applicant_type === 'camp_organizer' && 'Organizes athletic camps, clinics, or training programs.'}
                            {formData.applicant_type === 'academy' && 'Runs an athletic training academy or program.'}
                          </p>
                        </div>
                      )}
                      {formData.applicant_type === 'other' && (
                        <div className="mt-3 space-y-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-amber-300">
                              Please describe your role and how you help athletes so we can better understand your work.
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="other_type_description" className="text-sm font-medium">
                              What do you do & how does it help athletes? *
                            </Label>
                            <Textarea
                              id="other_type_description"
                              value={formData.other_type_description}
                              onChange={(e) => setFormData({...formData, other_type_description: e.target.value})}
                              placeholder="Describe your role, services, and the specific ways you support or benefit athletes..."
                              className="bg-background/50 border-white/10 focus:border-primary min-h-[100px]"
                              required
                            />
                            <p className="text-xs text-muted-foreground">
                              Be specific about the services you offer and the value you provide to athletes.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="experience_years" className="text-sm font-medium">Years of Experience</Label>
                      <Input
                        id="experience_years"
                        type="number"
                        min="0"
                        value={formData.experience_years}
                        onChange={(e) => setFormData({...formData, experience_years: e.target.value})}
                        className="bg-background/50 border-white/10 focus:border-primary h-12"
                        placeholder="5"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="athletes_represented" className="text-sm font-medium">Athletes Represented/Worked With</Label>
                      <Input
                        id="athletes_represented"
                        type="number"
                        min="0"
                        value={formData.athletes_represented}
                        onChange={(e) => setFormData({...formData, athletes_represented: e.target.value})}
                        className="bg-background/50 border-white/10 focus:border-primary h-12"
                        placeholder="10"
                      />
                    </div>
                  </div>

                  {/* NIL Compliance Section */}
                  <div className="space-y-4 p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Scale className="h-5 w-5 text-blue-400" />
                      <h3 className="font-semibold text-lg">NIL Compliance Information</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Help us understand your professional credentials and compliance status.
                    </p>

                    <div className="flex items-center justify-between py-3 border-b border-white/10">
                      <div>
                        <Label className="text-sm font-medium">Are you a licensed attorney?</Label>
                        <p className="text-xs text-muted-foreground">Licensed to practice law in any jurisdiction</p>
                      </div>
                      <Switch
                        checked={formData.is_licensed_attorney}
                        onCheckedChange={(checked) => setFormData({...formData, is_licensed_attorney: checked})}
                      />
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-white/10">
                      <div>
                        <Label className="text-sm font-medium">Do you have professional liability insurance?</Label>
                        <p className="text-xs text-muted-foreground">E&O or professional liability coverage</p>
                      </div>
                      <Switch
                        checked={formData.has_liability_insurance}
                        onCheckedChange={(checked) => setFormData({...formData, has_liability_insurance: checked})}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="nil_experience_years" className="text-sm font-medium">Years of NIL-Specific Experience</Label>
                      <Input
                        id="nil_experience_years"
                        type="number"
                        min="0"
                        value={formData.nil_experience_years}
                        onChange={(e) => setFormData({...formData, nil_experience_years: e.target.value})}
                        className="bg-background/50 border-white/10 focus:border-primary h-12"
                        placeholder="Years working specifically with NIL deals"
                      />
                    </div>

                    {/* States Licensed Multi-select */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">States Registered as Athlete Agent</Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Select all states where you are registered under the Uniform Athlete Agent Act (UAAA) or state equivalent
                      </p>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-between bg-background/50 border-white/10 hover:bg-background/70 h-12 text-left font-normal"
                          >
                            {selectedLicensedStates.length === 0 ? (
                              <span className="text-muted-foreground">Select states...</span>
                            ) : (
                              <span className="truncate">
                                {selectedLicensedStates.length} state{selectedLicensedStates.length !== 1 ? 's' : ''} selected
                              </span>
                            )}
                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0 bg-background border-white/20" align="start">
                          <ScrollArea className="h-64 p-2">
                            <div className="space-y-1">
                              {US_STATES.map((state) => (
                                <div
                                  key={state.code}
                                  className="flex items-center space-x-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer"
                                  onClick={() => handleLicensedStateToggle(state.code)}
                                >
                                  <Checkbox
                                    id={`licensed-${state.code}`}
                                    checked={selectedLicensedStates.includes(state.code)}
                                    onCheckedChange={() => handleLicensedStateToggle(state.code)}
                                    className="border-white/30 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
                                  />
                                  <label
                                    htmlFor={`licensed-${state.code}`}
                                    className="text-sm text-gray-300 cursor-pointer flex-1"
                                  >
                                    {state.name}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </PopoverContent>
                      </Popover>
                      {selectedLicensedStates.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {selectedLicensedStates.map((code) => {
                            const state = US_STATES.find(s => s.code === code);
                            return (
                              <Badge
                                key={code}
                                variant="secondary"
                                className="bg-blue-500/20 text-blue-400 border-blue-500/30 cursor-pointer hover:bg-blue-500/30"
                                onClick={() => handleLicensedStateToggle(code)}
                              >
                                {state?.name || code}
                                <X className="ml-1 h-3 w-3" />
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* SPARTA Compliance Acknowledgment */}
                    <div className="flex items-start space-x-3 pt-4 border-t border-white/10">
                      <Checkbox
                        id="sparta_compliance"
                        checked={formData.sparta_compliance_acknowledged}
                        onCheckedChange={(checked) => setFormData({...formData, sparta_compliance_acknowledged: checked as boolean})}
                        className="mt-1 border-white/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                      <div>
                        <Label htmlFor="sparta_compliance" className="text-sm font-medium cursor-pointer">
                          SPARTA Compliance Acknowledgment *
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          I acknowledge my understanding of the Sports Agent Responsibility and Trust Act (SPARTA), 
                          which prohibits deceptive practices in athlete representation and requires specific disclosures. 
                          I agree to comply with all applicable federal and state regulations.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Sports Focus */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Sports Focus (select all that apply)</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-between bg-background/50 border-white/10 hover:bg-background/70 h-12 text-left font-normal"
                        >
                          {selectedSports.length === 0 ? (
                            <span className="text-muted-foreground">Select sports...</span>
                          ) : (
                            <span className="truncate">
                              {selectedSports.length} sport{selectedSports.length !== 1 ? 's' : ''} selected
                            </span>
                          )}
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0 bg-background border-white/20" align="start">
                        <ScrollArea className="h-64 p-2">
                          <div className="space-y-1">
                            {availableSports.map((sport) => (
                              <div
                                key={sport}
                                className="flex items-center space-x-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer"
                                onClick={() => handleSportToggle(sport)}
                              >
                                <Checkbox
                                  id={`sport-${sport}`}
                                  checked={selectedSports.includes(sport)}
                                  onCheckedChange={() => handleSportToggle(sport)}
                                  className="border-white/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                />
                                <label
                                  htmlFor={`sport-${sport}`}
                                  className="text-sm text-gray-300 cursor-pointer flex-1"
                                >
                                  {sport}
                                </label>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </PopoverContent>
                    </Popover>
                    {selectedSports.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {selectedSports.map((sport) => (
                          <Badge
                            key={sport}
                            variant="secondary"
                            className="bg-primary/20 text-primary border-primary/30 cursor-pointer hover:bg-primary/30"
                            onClick={() => handleSportToggle(sport)}
                          >
                            {sport}
                            <X className="ml-1 h-3 w-3" />
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Geographic Focus */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Geographic Focus</Label>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="country" className="text-xs text-gray-400">Country</Label>
                        <Select
                          value={formData.country}
                          onValueChange={(value) => setFormData({...formData, country: value, state_province: ''})}
                        >
                          <SelectTrigger className="bg-background/50 border-white/10 focus:border-primary h-12">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent className="bg-background border-white/20 max-h-60">
                            {COUNTRIES.map((country) => (
                              <SelectItem key={country.code} value={country.code}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="state_province" className="text-xs text-gray-400">State/Province</Label>
                        {availableStates.length > 0 ? (
                          <Select
                            value={formData.state_province}
                            onValueChange={(value) => setFormData({...formData, state_province: value})}
                          >
                            <SelectTrigger className="bg-background/50 border-white/10 focus:border-primary h-12">
                              <SelectValue placeholder="Select state/province" />
                            </SelectTrigger>
                            <SelectContent className="bg-background border-white/20 max-h-60">
                              {availableStates.map((state) => (
                                <SelectItem key={state.code} value={state.code}>
                                  {state.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            id="state_province"
                            placeholder="Enter state/province"
                            value={formData.state_province}
                            onChange={(e) => setFormData({...formData, state_province: e.target.value})}
                            className="bg-background/50 border-white/10 focus:border-primary h-12"
                          />
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="city" className="text-xs text-gray-400">City</Label>
                        <Input
                          id="city"
                          placeholder="Enter city"
                          value={formData.city}
                          onChange={(e) => setFormData({...formData, city: e.target.value})}
                          className="bg-background/50 border-white/10 focus:border-primary h-12"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="why_join" className="text-sm font-medium">Why do you want to join? *</Label>
                    <Textarea
                      id="why_join"
                      placeholder="Tell us why you're interested in Players Lockeroom and how you plan to use the tools..."
                      value={formData.why_join}
                      onChange={(e) => setFormData({...formData, why_join: e.target.value})}
                      className="bg-background/50 border-white/10 focus:border-primary min-h-[120px]"
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="linkedin_url" className="text-sm font-medium">LinkedIn URL</Label>
                      <Input
                        id="linkedin_url"
                        placeholder="https://linkedin.com/in/..."
                        value={formData.linkedin_url}
                        onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                        className="bg-background/50 border-white/10 focus:border-primary h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website_url" className="text-sm font-medium">Website URL</Label>
                      <Input
                        id="website_url"
                        placeholder="https://..."
                        value={formData.website_url}
                        onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                        className="bg-background/50 border-white/10 focus:border-primary h-12"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="how_heard" className="text-sm font-medium">How did you hear about us?</Label>
                    <Input
                      id="how_heard"
                      value={formData.how_heard_about_us}
                      onChange={(e) => setFormData({...formData, how_heard_about_us: e.target.value})}
                      className="bg-background/50 border-white/10 focus:border-primary h-12"
                      placeholder="Social media, referral, etc."
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-6 text-lg rounded-xl shadow-[0_0_30px_rgba(0,255,0,0.2)] hover:shadow-[0_0_40px_rgba(0,255,0,0.3)] transition-all duration-300" 
                    size="lg"
                    disabled={isSubmitting || spotsRemaining === 0}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin mr-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>

                  <div className="text-xs text-center text-muted-foreground space-y-2">
                    <p>
                      By submitting, you agree to our Terms of Service and acknowledge our platform 
                      role clarification above.
                    </p>
                    <p className="flex items-center justify-center gap-1 text-yellow-500/80">
                      <Clock className="h-3 w-3" />
                      <span>Approved applicants have 7 days to complete payment or their spot may be offered to the next applicant.</span>
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#0d0d0d]">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground mb-4">
            Want to see the full feature breakdown?
          </p>
          <Button 
            variant="outline"
            size="lg"
            onClick={() => navigate('/agent/pricing')}
            className="border-primary/30 text-primary hover:bg-primary/10 font-semibold px-8"
          >
            View Pricing & Features
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AgentBetaLanding;